import { TrendingUp, ArrowLeft, Check, Copy, Code2, Sparkles, ExternalLink, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";
import logo from "@/assets/logo.png";
import SEO from "@/components/SEO";

interface IndicatorData {
  id: string;
  title: string;
  pine_script_code: string;
  is_active: boolean;
}

const Indicator = () => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [indicators, setIndicators] = useState<IndicatorData[]>([]);
  const [activeTab, setActiveTab] = useState<string>("");

  useEffect(() => {
    const fetchIndicators = async () => {
      const { data } = await supabase
        .from("indicators")
        .select("*")
        .order("created_at", { ascending: true });
      if (data && data.length > 0) {
        const mapped = data.map((d: any) => ({
          id: d.id,
          title: d.title,
          pine_script_code: d.pine_script_code,
          is_active: d.is_active,
        }));
        setIndicators(mapped);
        const active = mapped.find((i) => i.is_active);
        setActiveTab(active?.id || mapped[0].id);
      }
    };
    fetchIndicators();
  }, []);

  const selectedIndicator = indicators.find((i) => i.id === activeTab);

  const handleCopy = () => {
    if (!selectedIndicator) return;
    navigator.clipboard.writeText(selectedIndicator.pine_script_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (indicators.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center animate-pulse">
            <TrendingUp className="w-6 h-6 text-primary" />
          </div>
          <p className="text-muted-foreground">Loading indicators...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Free TradingView Indicator — Fitawrari FX SK System"
        description="Get the Fitawrari FX TradingView Pine Script indicator built around the SK System for cleaner entries, trend confirmation, and risk control."
        path="/indicator"
      />
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src={logo} alt="Fitawrari FX" className="h-10 w-auto" />
            <span className="text-xl font-display font-semibold text-foreground">
              Fitawrari FX
            </span>
          </a>
          <Button variant="outline" onClick={() => navigate("/")} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </div>
      </nav>

      {/* Indicator Tabs - Redesigned */}
      <div className="fixed top-[57px] left-0 right-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-2 py-3 overflow-x-auto scrollbar-hide">
            {indicators.map((ind) => {
              const isActive = activeTab === ind.id;
              return (
                <motion.button
                  key={ind.id}
                  onClick={() => setActiveTab(ind.id)}
                  whileHover={{ scale: 1.06 }}
                  whileTap={{ scale: 0.95 }}
                  className={`
                    relative flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-300
                    ${isActive
                      ? "bg-primary text-primary-foreground shadow-lg shadow-primary/30"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/80 border border-transparent hover:border-border"
                    }
                  `}
                >
                  <TrendingUp className={`w-4 h-4 transition-transform duration-300 ${isActive ? "rotate-12" : ""}`} />
                  {ind.title}
                  {ind.is_active && (
                    <span className={`
                      text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider
                      ${isActive ? "bg-primary-foreground/20 text-primary-foreground" : "bg-primary/10 text-primary"}
                    `}>
                      Live
                    </span>
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {selectedIndicator && (
          <motion.div
            key={selectedIndicator.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
          >
            {/* Hero */}
            <section className="pt-[140px] pb-16 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent pointer-events-none" />
              <div className="container mx-auto px-4 text-center relative">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 }}
                  className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-medium mb-6"
                >
                  <Sparkles className="w-4 h-4" />
                  TradingView Indicator
                </motion.div>
                <h1 className="font-display text-4xl sm:text-5xl md:text-7xl font-bold mb-6 leading-tight">
                  {selectedIndicator.title}
                </h1>
                <p className="text-muted-foreground max-w-2xl mx-auto mb-8 text-lg">
                  Professional trading indicator for TradingView. Copy the Pine Script code below and add it to your charts.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <Button size="lg" onClick={handleCopy} className="gap-2 shadow-lg shadow-primary/20">
                    {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                    {copied ? "Copied!" : "Copy Pine Script"}
                  </Button>
                  <Button size="lg" variant="outline" asChild className="gap-2">
                    <a href="https://www.tradingview.com/u/fitawrarifx/#published-scripts" target="_blank" rel="noopener noreferrer">
                      <TrendingUp className="w-5 h-5" />
                      Follow on TradingView
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                </div>
              </div>
            </section>

            {/* Pine Script Code */}
            <section className="py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Code2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h2 className="font-display text-2xl font-bold">Pine Script Code</h2>
                      <p className="text-muted-foreground text-sm">Copy into TradingView's Pine Script Editor</p>
                    </div>
                  </div>

                  <div className="rounded-2xl overflow-hidden border border-border bg-card shadow-lg">
                    <div className="flex items-center justify-between px-5 py-3 bg-muted/50 border-b border-border">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-destructive/50" />
                        <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
                        <div className="w-3 h-3 rounded-full bg-green-500/50" />
                        <span className="ml-3 text-xs text-muted-foreground font-mono tracking-wide">
                          {selectedIndicator.title.replace(/\s+/g, "_").toLowerCase()}.pine
                        </span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={handleCopy} className="gap-2 text-xs text-muted-foreground hover:text-foreground">
                        {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                        {copied ? "Copied!" : "Copy"}
                      </Button>
                    </div>
                    <div className="p-5 max-h-[420px] overflow-y-auto bg-card">
                      <pre className="text-sm font-mono text-muted-foreground whitespace-pre-wrap break-words leading-relaxed">
                        {selectedIndicator.pine_script_code}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* How to Add */}
            <section className="py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                  <h2 className="font-display text-3xl md:text-4xl font-bold mb-3">How to Add the Indicator</h2>
                  <p className="text-muted-foreground">Two ways to get the indicator on your charts</p>
                </div>
                <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                  {/* Method 1 */}
                  <Card className="bg-card border-border overflow-hidden group hover:shadow-lg transition-shadow duration-300">
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                          <Code2 className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-primary uppercase tracking-wider">Method 1</p>
                          <h3 className="font-display text-lg font-bold">Add via Pine Script</h3>
                        </div>
                      </div>
                      <CardContent className="p-0 space-y-3">
                        {[
                          "Open TradingView and go to your chart",
                          "Click the Pine Script Editor icon at the bottom",
                          "Copy the code above and paste it into the editor",
                          "Click \"Add to Chart\" to activate"
                        ].map((step, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                              <span className="text-xs font-bold text-primary">{i + 1}</span>
                            </div>
                            <p className="text-muted-foreground text-sm">{step}</p>
                          </div>
                        ))}
                      </CardContent>
                    </div>
                  </Card>

                  {/* Method 2 */}
                  <Card className="bg-card border-border overflow-hidden group hover:shadow-lg transition-shadow duration-300">
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                          <TrendingUp className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-primary uppercase tracking-wider">Method 2</p>
                          <h3 className="font-display text-lg font-bold">Follow on TradingView</h3>
                        </div>
                      </div>
                      <CardContent className="p-0 space-y-3">
                        {[
                          "Visit our TradingView profile and follow us",
                          "Find the indicator in our published scripts",
                          "Click \"Add to Favorites\" to save it",
                          "Get automatic updates on new versions"
                        ].map((step, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                              <span className="text-xs font-bold text-primary">{i + 1}</span>
                            </div>
                            <p className="text-muted-foreground text-sm">{step}</p>
                          </div>
                        ))}
                        <Button asChild className="w-full mt-3 gap-2">
                          <a href="https://www.tradingview.com/u/fitawrarifx/#published-scripts" target="_blank" rel="noopener noreferrer">
                            Follow Our Profile
                            <ChevronRight className="w-4 h-4" />
                          </a>
                        </Button>
                      </CardContent>
                    </div>
                  </Card>
                </div>
              </div>
            </section>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer className="py-8 border-t border-border">
        <div className="container mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-muted-foreground text-sm">© {new Date().getFullYear()} Fitawrari FX. All rights reserved.</p>
          <p className="text-muted-foreground text-sm">Built for professional traders</p>
        </div>
      </footer>
    </div>
  );
};

export default Indicator;